import type {
  AlertRule,
  Bar,
  EarningsItem,
  IndexQuote,
  NewsItem,
  Position,
  Range,
  Security,
  SecurityType,
  Exchange,
  SectorPerf,
} from '@/lib/types';
import { clamp } from '@/lib/format';

type Raw = [
  symbol: string,
  name: string,
  type: SecurityType,
  exchange: Exchange,
  sector: string,
  price: number,
  capBillions: number,
  pe: number,
  dividendYield: number,
];

// A hand-built slice of the US tape. The live universe is ~13,200 securities
// from the Nasdaq Trader directory; these are the names the demo screens use.
const RAW: Raw[] = [
  ['AAPL', 'Apple Inc.', 'stock', 'NASDAQ', 'Technology', 232.1, 3480, 35.2, 0.44],
  ['MSFT', 'Microsoft Corporation', 'stock', 'NASDAQ', 'Technology', 468.4, 3480, 36.8, 0.68],
  ['NVDA', 'NVIDIA Corporation', 'stock', 'NASDAQ', 'Technology', 187.6, 4580, 52.4, 0.03],
  ['AMZN', 'Amazon.com, Inc.', 'stock', 'NASDAQ', 'Consumer Discretionary', 231.2, 2440, 38.1, 0],
  ['GOOGL', 'Alphabet Inc.', 'stock', 'NASDAQ', 'Communication Services', 201.4, 2450, 26.3, 0.42],
  ['META', 'Meta Platforms, Inc.', 'stock', 'NASDAQ', 'Communication Services', 612.3, 1550, 27.8, 0.34],
  ['TSLA', 'Tesla, Inc.', 'stock', 'NASDAQ', 'Consumer Discretionary', 421.8, 1350, 118.4, 0],
  ['BRK.B', 'Berkshire Hathaway Inc.', 'stock', 'NYSE', 'Financials', 512.6, 1100, 14.2, 0],
  ['AVGO', 'Broadcom Inc.', 'stock', 'NASDAQ', 'Technology', 372.5, 1740, 61.2, 0.62],
  ['LLY', 'Eli Lilly and Company', 'stock', 'NYSE', 'Health Care', 892.4, 848, 74.6, 0.58],
  ['JPM', 'JPMorgan Chase & Co.', 'stock', 'NYSE', 'Financials', 298.3, 828, 15.1, 1.92],
  ['V', 'Visa Inc.', 'stock', 'NYSE', 'Financials', 342.7, 668, 31.4, 0.68],
  ['XOM', 'Exxon Mobil Corporation', 'stock', 'NYSE', 'Energy', 118.4, 512, 14.8, 3.32],
  ['UNH', 'UnitedHealth Group Incorporated', 'stock', 'NYSE', 'Health Care', 342.6, 315, 19.4, 2.42],
  ['MA', 'Mastercard Incorporated', 'stock', 'NYSE', 'Financials', 578.2, 528, 36.2, 0.52],
  ['COST', 'Costco Wholesale Corporation', 'stock', 'NASDAQ', 'Consumer Staples', 942.1, 418, 52.6, 0.48],
  ['HD', 'The Home Depot, Inc.', 'stock', 'NYSE', 'Consumer Discretionary', 412.3, 409, 26.1, 2.22],
  ['PG', 'The Procter & Gamble Company', 'stock', 'NYSE', 'Consumer Staples', 168.9, 398, 26.4, 2.42],
  ['JNJ', 'Johnson & Johnson', 'stock', 'NYSE', 'Health Care', 158.2, 381, 17.8, 3.12],
  ['WMT', 'Walmart Inc.', 'stock', 'NYSE', 'Consumer Staples', 102.4, 822, 37.9, 0.82],
  ['ABBV', 'AbbVie Inc.', 'stock', 'NYSE', 'Health Care', 212.7, 376, 19.2, 3.08],
  ['NFLX', 'Netflix, Inc.', 'stock', 'NASDAQ', 'Communication Services', 1124.6, 478, 44.8, 0],
  ['CRM', 'Salesforce, Inc.', 'stock', 'NYSE', 'Technology', 284.5, 272, 44.1, 0.56],
  ['AMD', 'Advanced Micro Devices, Inc.', 'stock', 'NASDAQ', 'Technology', 168.4, 273, 46.2, 0],
  ['ORCL', 'Oracle Corporation', 'stock', 'NYSE', 'Technology', 248.9, 692, 42.4, 0.72],
  ['ADBE', 'Adobe Inc.', 'stock', 'NASDAQ', 'Technology', 412.8, 176, 23.6, 0],
  ['PEP', 'PepsiCo, Inc.', 'stock', 'NASDAQ', 'Consumer Staples', 148.6, 204, 21.4, 3.62],
  ['KO', 'The Coca-Cola Company', 'stock', 'NYSE', 'Consumer Staples', 68.4, 295, 24.8, 2.92],
  ['TMO', 'Thermo Fisher Scientific Inc.', 'stock', 'NYSE', 'Health Care', 512.4, 194, 32.6, 0.32],
  ['CSCO', 'Cisco Systems, Inc.', 'stock', 'NASDAQ', 'Technology', 72.8, 290, 26.4, 2.18],
  ['INTC', 'Intel Corporation', 'stock', 'NASDAQ', 'Technology', 38.6, 166, 0, 1.32],
  ['QCOM', 'QUALCOMM Incorporated', 'stock', 'NASDAQ', 'Technology', 172.4, 188, 16.2, 1.98],
  ['TXN', 'Texas Instruments Incorporated', 'stock', 'NASDAQ', 'Technology', 198.2, 181, 34.8, 2.72],
  ['MU', 'Micron Technology, Inc.', 'stock', 'NASDAQ', 'Technology', 248.6, 276, 24.6, 0.42],
  ['AMAT', 'Applied Materials, Inc.', 'stock', 'NASDAQ', 'Technology', 218.4, 174, 26.2, 0.72],
  ['PLTR', 'Palantir Technologies Inc.', 'stock', 'NASDAQ', 'Technology', 182.3, 428, 218.4, 0],
  ['UBER', 'Uber Technologies, Inc.', 'stock', 'NYSE', 'Industrials', 98.4, 206, 31.8, 0],
  ['SHOP', 'Shopify Inc.', 'stock', 'NYSE', 'Technology', 162.8, 211, 72.4, 0],
  ['COIN', 'Coinbase Global, Inc.', 'stock', 'NASDAQ', 'Financials', 342.6, 86, 48.2, 0],
  ['SOFI', 'SoFi Technologies, Inc.', 'stock', 'NASDAQ', 'Financials', 28.4, 32, 42.6, 0],
  ['ABNB', 'Airbnb, Inc.', 'stock', 'NASDAQ', 'Consumer Discretionary', 132.6, 82, 34.2, 0],
  ['SNOW', 'Snowflake Inc.', 'stock', 'NYSE', 'Technology', 248.6, 82, 0, 0],
  ['CRWD', 'CrowdStrike Holdings, Inc.', 'stock', 'NASDAQ', 'Technology', 512.4, 126, 0, 0],
  ['PANW', 'Palo Alto Networks, Inc.', 'stock', 'NASDAQ', 'Technology', 218.6, 146, 48.2, 0],
  ['NOW', 'ServiceNow, Inc.', 'stock', 'NYSE', 'Technology', 1082.4, 224, 128.4, 0],
  ['INTU', 'Intuit Inc.', 'stock', 'NASDAQ', 'Technology', 682.4, 191, 52.6, 0.68],
  ['BKNG', 'Booking Holdings Inc.', 'stock', 'NASDAQ', 'Consumer Discretionary', 5428.6, 178, 28.4, 0.62],
  ['PFE', 'Pfizer Inc.', 'stock', 'NYSE', 'Health Care', 26.8, 152, 18.4, 6.28],
  ['MRK', 'Merck & Co., Inc.', 'stock', 'NYSE', 'Health Care', 98.4, 249, 14.2, 3.32],
  ['BAC', 'Bank of America Corporation', 'stock', 'NYSE', 'Financials', 48.6, 372, 13.8, 2.42],
  ['WFC', 'Wells Fargo & Company', 'stock', 'NYSE', 'Financials', 78.4, 248, 14.2, 2.12],
  ['GS', 'The Goldman Sachs Group, Inc.', 'stock', 'NYSE', 'Financials', 612.4, 192, 15.6, 1.82],
  ['MS', 'Morgan Stanley', 'stock', 'NYSE', 'Financials', 148.6, 238, 17.2, 2.42],
  ['C', 'Citigroup Inc.', 'stock', 'NYSE', 'Financials', 78.2, 146, 12.4, 2.82],
  ['BLK', 'BlackRock, Inc.', 'stock', 'NYSE', 'Financials', 1084.2, 168, 24.6, 1.92],
  ['SCHW', 'The Charles Schwab Corporation', 'stock', 'NYSE', 'Financials', 92.4, 164, 22.8, 1.42],
  ['CAT', 'Caterpillar Inc.', 'stock', 'NYSE', 'Industrials', 412.6, 198, 18.2, 1.42],
  ['DE', 'Deere & Company', 'stock', 'NYSE', 'Industrials', 486.2, 132, 22.4, 1.32],
  ['BA', 'The Boeing Company', 'stock', 'NYSE', 'Industrials', 218.4, 164, 0, 0],
  ['GE', 'GE Aerospace', 'stock', 'NYSE', 'Industrials', 286.4, 308, 38.2, 0.42],
  ['LMT', 'Lockheed Martin Corporation', 'stock', 'NYSE', 'Industrials', 512.8, 122, 18.4, 2.62],
  ['RTX', 'RTX Corporation', 'stock', 'NYSE', 'Industrials', 168.4, 224, 32.6, 1.52],
  ['NKE', 'NIKE, Inc.', 'stock', 'NYSE', 'Consumer Discretionary', 72.6, 108, 34.2, 2.12],
  ['SBUX', 'Starbucks Corporation', 'stock', 'NASDAQ', 'Consumer Discretionary', 88.4, 100, 32.4, 2.62],
  ['MCD', "McDonald's Corporation", 'stock', 'NYSE', 'Consumer Discretionary', 302.4, 216, 24.8, 2.32],
  ['DIS', 'The Walt Disney Company', 'stock', 'NYSE', 'Communication Services', 112.8, 204, 20.4, 0.82],
  ['VZ', 'Verizon Communications Inc.', 'stock', 'NYSE', 'Communication Services', 42.6, 179, 9.2, 6.42],
  ['T', 'AT&T Inc.', 'stock', 'NYSE', 'Communication Services', 26.8, 192, 11.4, 4.12],
  ['TMUS', 'T-Mobile US, Inc.', 'stock', 'NASDAQ', 'Communication Services', 248.6, 288, 24.2, 1.42],
  ['AMT', 'American Tower Corporation', 'stock', 'NYSE', 'Real Estate', 198.4, 92, 28.6, 3.22],
  ['PLD', 'Prologis, Inc.', 'stock', 'NYSE', 'Real Estate', 112.4, 104, 32.4, 3.42],
  ['NEE', 'NextEra Energy, Inc.', 'stock', 'NYSE', 'Utilities', 78.6, 162, 22.4, 2.82],
  ['DUK', 'Duke Energy Corporation', 'stock', 'NYSE', 'Utilities', 118.4, 92, 18.2, 3.62],
  ['LIN', 'Linde plc', 'stock', 'NASDAQ', 'Materials', 468.2, 221, 32.4, 1.22],
  ['FCX', 'Freeport-McMoRan Inc.', 'stock', 'NYSE', 'Materials', 42.8, 61, 32.6, 1.42],
  ['NEM', 'Newmont Corporation', 'stock', 'NYSE', 'Materials', 62.4, 71, 18.4, 1.62],
  ['OXY', 'Occidental Petroleum Corporation', 'stock', 'NYSE', 'Energy', 48.6, 46, 16.2, 1.82],
  ['CVX', 'Chevron Corporation', 'stock', 'NYSE', 'Energy', 158.4, 288, 14.6, 4.12],
  ['COP', 'ConocoPhillips', 'stock', 'NYSE', 'Energy', 102.6, 128, 12.8, 3.02],
  ['SLB', 'SLB', 'stock', 'NYSE', 'Energy', 38.4, 58, 12.4, 2.82],
  ['EOG', 'EOG Resources, Inc.', 'stock', 'NYSE', 'Energy', 118.6, 68, 10.8, 3.42],
  ['F', 'Ford Motor Company', 'stock', 'NYSE', 'Consumer Discretionary', 12.4, 49, 8.6, 5.42],
  ['GM', 'General Motors Company', 'stock', 'NYSE', 'Consumer Discretionary', 58.2, 54, 7.8, 1.02],
  ['RIVN', 'Rivian Automotive, Inc.', 'stock', 'NASDAQ', 'Consumer Discretionary', 14.8, 17, 0, 0],
  ['LCID', 'Lucid Group, Inc.', 'stock', 'NASDAQ', 'Consumer Discretionary', 3.2, 10, 0, 0],
  ['NIO', 'NIO Inc.', 'stock', 'NYSE', 'Consumer Discretionary', 6.4, 13, 0, 0],
  ['XPEV', 'XPeng Inc.', 'stock', 'NYSE', 'Consumer Discretionary', 22.6, 21, 0, 0],
  ['ARM', 'Arm Holdings plc', 'stock', 'NASDAQ', 'Technology', 148.6, 156, 142.4, 0],
  ['SMCI', 'Super Micro Computer, Inc.', 'stock', 'NASDAQ', 'Technology', 42.8, 25, 18.4, 0],
  ['MSTR', 'Strategy Inc.', 'stock', 'NASDAQ', 'Technology', 298.4, 82, 0, 0],
  ['HOOD', 'Robinhood Markets, Inc.', 'stock', 'NASDAQ', 'Financials', 118.6, 104, 52.4, 0],
  ['APP', 'AppLovin Corporation', 'stock', 'NASDAQ', 'Technology', 612.4, 206, 62.4, 0],
  ['AXON', 'Axon Enterprise, Inc.', 'stock', 'NASDAQ', 'Industrials', 682.4, 52, 112.4, 0],
  ['VRT', 'Vertiv Holdings Co', 'stock', 'NYSE', 'Industrials', 148.6, 56, 48.2, 0.12],
  ['ANET', 'Arista Networks, Inc.', 'stock', 'NYSE', 'Technology', 128.4, 162, 48.6, 0],
  ['DDOG', 'Datadog, Inc.', 'stock', 'NASDAQ', 'Technology', 142.6, 48, 0, 0],
  ['ZS', 'Zscaler, Inc.', 'stock', 'NASDAQ', 'Technology', 312.4, 48, 0, 0],
  ['MDB', 'MongoDB, Inc.', 'stock', 'NASDAQ', 'Technology', 342.8, 28, 0, 0],
  ['NET', 'Cloudflare, Inc.', 'stock', 'NYSE', 'Technology', 198.4, 68, 0, 0],
  ['RBLX', 'Roblox Corporation', 'stock', 'NYSE', 'Communication Services', 128.6, 82, 0, 0],
  ['SPOT', 'Spotify Technology S.A.', 'stock', 'NYSE', 'Communication Services', 682.4, 138, 62.4, 0],
  ['DASH', 'DoorDash, Inc.', 'stock', 'NASDAQ', 'Consumer Discretionary', 248.6, 104, 82.4, 0],
  ['PINS', 'Pinterest, Inc.', 'stock', 'NYSE', 'Communication Services', 32.8, 22, 42.6, 0],
  ['SNAP', 'Snap Inc.', 'stock', 'NYSE', 'Communication Services', 8.6, 15, 0, 0],
  ['ROKU', 'Roku, Inc.', 'stock', 'NASDAQ', 'Communication Services', 82.4, 12, 0, 0],
  ['ETSY', 'Etsy, Inc.', 'stock', 'NASDAQ', 'Consumer Discretionary', 58.4, 7, 24.2, 0],
  ['EBAY', 'eBay Inc.', 'stock', 'NASDAQ', 'Consumer Discretionary', 82.6, 41, 18.4, 1.62],
  ['SPY', 'SPDR S&P 500 ETF Trust', 'etf', 'NYSE', 'ETF', 682.4, 648, 26.4, 1.18],
  ['QQQ', 'Invesco QQQ Trust', 'etf', 'NASDAQ', 'ETF', 612.8, 348, 32.8, 0.58],
  ['DIA', 'SPDR Dow Jones Industrial Average ETF Trust', 'etf', 'NYSE', 'ETF', 468.2, 38, 24.6, 1.42],
  ['IWM', 'iShares Russell 2000 ETF', 'etf', 'NYSE', 'ETF', 248.6, 68, 18.4, 1.12],
  ['VOO', 'Vanguard S&P 500 ETF', 'etf', 'NYSE', 'ETF', 628.4, 582, 26.4, 1.22],
  ['VTI', 'Vanguard Total Stock Market ETF', 'etf', 'NYSE', 'ETF', 312.6, 468, 26.2, 1.18],
  ['IVV', 'iShares Core S&P 500 ETF', 'etf', 'NYSE', 'ETF', 684.2, 582, 26.4, 1.22],
  ['VEA', 'Vanguard FTSE Developed Markets ETF', 'etf', 'NYSE', 'ETF', 58.4, 148, 16.2, 2.82],
  ['VWO', 'Vanguard FTSE Emerging Markets ETF', 'etf', 'NYSE', 'ETF', 48.6, 82, 14.2, 2.62],
  ['VGT', 'Vanguard Information Technology ETF', 'etf', 'NYSE', 'ETF', 728.4, 92, 36.4, 0.52],
  ['XLK', 'Technology Select Sector SPDR Fund', 'etf', 'NYSE', 'ETF', 268.4, 78, 36.2, 0.62],
  ['XLF', 'Financial Select Sector SPDR Fund', 'etf', 'NYSE', 'ETF', 52.8, 48, 17.4, 1.52],
  ['XLE', 'Energy Select Sector SPDR Fund', 'etf', 'NYSE', 'ETF', 92.6, 38, 15.2, 3.12],
  ['XLV', 'Health Care Select Sector SPDR Fund', 'etf', 'NYSE', 'ETF', 148.2, 38, 18.2, 1.62],
  ['XLY', 'Consumer Discretionary Select Sector SPDR Fund', 'etf', 'NYSE', 'ETF', 218.4, 22, 28.4, 0.72],
  ['XLP', 'Consumer Staples Select Sector SPDR Fund', 'etf', 'NYSE', 'ETF', 82.4, 15, 22.4, 2.42],
  ['XLI', 'Industrial Select Sector SPDR Fund', 'etf', 'NYSE', 'ETF', 142.6, 22, 24.2, 1.32],
  ['XLU', 'Utilities Select Sector SPDR Fund', 'etf', 'NYSE', 'ETF', 82.8, 18, 20.4, 2.82],
  ['XLB', 'Materials Select Sector SPDR Fund', 'etf', 'NYSE', 'ETF', 92.4, 6, 22.6, 1.82],
  ['XLRE', 'Real Estate Select Sector SPDR Fund', 'etf', 'NYSE', 'ETF', 42.6, 7, 32.4, 3.12],
  ['XLC', 'Communication Services Select Sector SPDR Fund', 'etf', 'NYSE', 'ETF', 112.4, 22, 24.6, 0.82],
  ['SMH', 'VanEck Semiconductor ETF', 'etf', 'NASDAQ', 'ETF', 312.6, 28, 42.4, 0.42],
  ['SOXX', 'iShares Semiconductor ETF', 'etf', 'NASDAQ', 'ETF', 248.4, 14, 42.8, 0.62],
  ['ARKK', 'ARK Innovation ETF', 'etf', 'NYSE', 'ETF', 78.6, 8, 0, 0],
  ['TQQQ', 'ProShares UltraPro QQQ', 'etf', 'NASDAQ', 'ETF', 92.4, 28, 0, 0],
  ['SQQQ', 'ProShares UltraPro Short QQQ', 'etf', 'NASDAQ', 'ETF', 6.8, 4, 0, 0],
  ['GLD', 'SPDR Gold Shares', 'etf', 'NYSE', 'ETF', 342.6, 82, 0, 0],
  ['SLV', 'iShares Silver Trust', 'etf', 'NYSE', 'ETF', 42.8, 16, 0, 0],
  ['USO', 'United States Oil Fund, LP', 'etf', 'NYSE', 'ETF', 78.4, 1, 0, 0],
  ['TLT', 'iShares 20+ Year Treasury Bond ETF', 'etf', 'NASDAQ', 'ETF', 92.6, 48, 0, 4.12],
  ['IEF', 'iShares 7-10 Year Treasury Bond ETF', 'etf', 'NASDAQ', 'ETF', 98.4, 28, 0, 3.42],
  ['HYG', 'iShares iBoxx $ High Yield Corporate Bond ETF', 'etf', 'NYSE', 'ETF', 78.6, 16, 0, 5.82],
  ['LQD', 'iShares iBoxx $ Investment Grade Corporate Bond ETF', 'etf', 'NYSE', 'ETF', 112.4, 32, 0, 4.42],
  ['EEM', 'iShares MSCI Emerging Markets ETF', 'etf', 'NYSE', 'ETF', 48.2, 18, 14.6, 2.42],
  ['EFA', 'iShares MSCI EAFE ETF', 'etf', 'NYSE', 'ETF', 88.4, 52, 16.4, 2.62],
  ['VNQ', 'Vanguard Real Estate ETF', 'etf', 'NYSE', 'ETF', 92.6, 32, 32.4, 3.62],
  ['SCHD', 'Schwab U.S. Dividend Equity ETF', 'etf', 'NYSE', 'ETF', 28.6, 68, 17.4, 3.42],
  ['VYM', 'Vanguard High Dividend Yield ETF', 'etf', 'NYSE', 'ETF', 132.4, 58, 17.8, 2.62],
  ['JEPI', 'JPMorgan Equity Premium Income ETF', 'etf', 'NYSE', 'ETF', 58.4, 38, 22.4, 7.12],
  ['JEPQ', 'JPMorgan Nasdaq Equity Premium Income ETF', 'etf', 'NASDAQ', 'ETF', 56.8, 28, 28.4, 8.42],
  ['QYLD', 'Global X Nasdaq 100 Covered Call ETF', 'etf', 'NASDAQ', 'ETF', 18.4, 8, 0, 11.42],
  ['BND', 'Vanguard Total Bond Market ETF', 'etf', 'NASDAQ', 'ETF', 72.8, 118, 0, 3.82],
  ['VXUS', 'Vanguard Total International Stock ETF', 'etf', 'NASDAQ', 'ETF', 68.4, 82, 16.2, 2.72],
  ['IEMG', 'iShares Core MSCI Emerging Markets ETF', 'etf', 'NYSE', 'ETF', 58.6, 78, 14.8, 2.52],
  ['IWF', 'iShares Russell 1000 Growth ETF', 'etf', 'NYSE', 'ETF', 412.8, 98, 32.4, 0.62],
  ['IWD', 'iShares Russell 1000 Value ETF', 'etf', 'NYSE', 'ETF', 198.6, 58, 18.4, 2.12],
  ['MTUM', 'iShares MSCI USA Momentum Factor ETF', 'etf', 'NYSE', 'ETF', 242.4, 18, 28.4, 1.02],
  ['QUAL', 'iShares MSCI USA Quality Factor ETF', 'etf', 'NYSE', 'ETF', 182.6, 48, 24.2, 1.22],
  ['USMV', 'iShares MSCI USA Min Vol Factor ETF', 'etf', 'NYSE', 'ETF', 92.4, 24, 22.4, 1.82],
  ['SPLV', 'Invesco S&P 500 Low Volatility ETF', 'etf', 'NYSE', 'ETF', 72.8, 8, 21.4, 2.12],
];

const SECTOR_BLURB: Record<string, string> = {
  Technology:
    'designs, builds and licenses technology products and services for businesses and consumers around the world.',
  'Communication Services':
    'operates platforms and networks that connect people, content and advertisers at global scale.',
  'Consumer Discretionary':
    'sells discretionary goods and services where demand is closely tied to the consumer cycle.',
  'Consumer Staples':
    'sells everyday products with steady demand through good times and bad.',
  Financials:
    'provides banking, payments, lending or capital-markets services across retail and institutional clients.',
  'Health Care':
    'develops medicines, devices or care services addressing large and often regulated markets.',
  Industrials:
    'builds machinery, equipment or infrastructure sold into industrial and commercial end markets.',
  Energy:
    'produces, refines or services oil and gas assets, with earnings tied to commodity prices.',
  Materials:
    'supplies raw and processed materials to industrial, construction and consumer markets.',
  Utilities:
    'operates regulated electric, gas or water infrastructure with long-lived assets and stable demand.',
  'Real Estate':
    'owns and operates income-producing property, typically in a real-estate investment trust structure.',
};

const SECTOR_MARGIN: Record<string, [number, number]> = {
  Technology: [0.18, 0.34],
  'Communication Services': [0.14, 0.3],
  'Consumer Discretionary': [0.04, 0.16],
  'Consumer Staples': [0.08, 0.18],
  Financials: [0.2, 0.36],
  'Health Care': [0.1, 0.28],
  Industrials: [0.07, 0.16],
  Energy: [0.06, 0.16],
  Materials: [0.06, 0.16],
  Utilities: [0.1, 0.2],
  'Real Estate': [0.12, 0.28],
  ETF: [0, 0],
};

const INDUSTRIES: Record<string, string[]> = {
  Technology: ['Software', 'Semiconductors', 'Hardware', 'Cloud infrastructure', 'IT services'],
  'Communication Services': ['Internet platforms', 'Media', 'Telecom', 'Interactive entertainment'],
  'Consumer Discretionary': ['E-commerce', 'Autos', 'Restaurants', 'Retail', 'Travel'],
  'Consumer Staples': ['Packaged foods', 'Beverages', 'Household products', 'Retail'],
  Financials: ['Banks', 'Payments', 'Capital markets', 'Asset management', 'Fintech'],
  'Health Care': ['Pharmaceuticals', 'Biotechnology', 'Medical devices', 'Managed care'],
  Industrials: ['Aerospace & defense', 'Machinery', 'Transportation', 'Electrical equipment'],
  Energy: ['Integrated oil & gas', 'Exploration & production', 'Oil services'],
  Materials: ['Chemicals', 'Mining', 'Metals'],
  Utilities: ['Electric utilities', 'Multi-utilities'],
  'Real Estate': ['REITs', 'Industrial real estate', 'Data centers'],
  ETF: ['Broad market', 'Sector', 'Factor', 'Fixed income', 'Commodities', 'International'],
};

function hashString(value: string): number {
  let h = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function round(value: number, digits = 2): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function buildSecurity(raw: Raw, index: number): Security {
  const [symbol, name, type, exchange, sector, price, capBillions, pe, dividendYield] = raw;
  const rand = mulberry32(hashString(symbol));
  const cap = capBillions * 1e9;
  const changePct = round((rand() - 0.48) * 3.2 + (rand() > 0.92 ? (rand() - 0.4) * 5 : 0), 2);
  const change = round((price * changePct) / 100, 2);
  const shares = cap / price;
  const volume = Math.round(shares * (type === 'etf' ? 0.004 + rand() * 0.02 : 0.002 + rand() * 0.016));
  const avgVolume = Math.round(volume * (0.72 + rand() * 0.8));
  const beta = round(type === 'etf' ? 0.55 + rand() * 0.7 : 0.6 + rand() * 1.25, 2);
  const [marginLow, marginHigh] = SECTOR_MARGIN[sector] ?? [0.08, 0.2];
  const netMargin = marginLow + rand() * (marginHigh - marginLow);
  const revenueGrowth = round(-4 + rand() * 42, 1);
  const grossMargin = round(type === 'etf' ? 0 : 24 + rand() * 52, 1);
  const roe = round(type === 'etf' ? 0 : 4 + rand() * 38, 1);
  const rsi = round(27 + rand() * 51, 1);
  const valueScore = Math.round(clamp(78 - (pe > 0 ? Math.min(pe / 3.2, 38) : 6) + rand() * 24, 5, 98));
  const growthScore = Math.round(clamp(32 + revenueGrowth * 1.2 + rand() * 18, 5, 98));
  const qualityScore = Math.round(clamp(30 + netMargin * 130 + rand() * 20, 5, 98));
  const momentumScore = Math.round(clamp(30 + changePct * 8 + (rsi - 50) * 0.5 + rand() * 20, 5, 98));
  const score = Math.round(
    valueScore * 0.25 + growthScore * 0.3 + qualityScore * 0.25 + momentumScore * 0.2,
  );

  const week52High = round(price * (1.06 + rand() * 0.34), 2);
  const week52Low = round(price * (1 - 0.06 - rand() * 0.34), 2);
  const eps = pe > 0 ? round(price / pe, 2) : round(-(rand() * 2.4), 2);

  const spark: number[] = [];
  let walk = price * (1 - (rand() - 0.45) * 0.09);
  for (let i = 0; i < 24; i += 1) {
    walk *= 1 + (rand() - 0.5) * 0.013;
    spark.push(round(walk, 2));
  }
  spark.push(price);

  const industryList = INDUSTRIES[sector] ?? ['Diversified'];
  const industry = industryList[index % industryList.length];
  const description =
    type === 'etf'
      ? `${name} is an exchange-traded fund listed on the ${exchange}. It gives investors a single, tradable way to hold a diversified basket of ${industry.toLowerCase()} exposure, with holdings and weights reset by the fund manager.`
      : `${name} is a ${sector.toLowerCase()} company listed on the ${exchange}. The company ${SECTOR_BLURB[sector] ?? 'operates across diversified end markets.'}`;

  return {
    symbol,
    name,
    type,
    exchange,
    sector,
    industry,
    price,
    change,
    changePct,
    volume,
    avgVolume,
    marketCap: cap,
    pe: pe > 0 ? pe : null,
    forwardPe: pe > 0 ? round(pe * (0.82 + rand() * 0.2), 1) : null,
    dividendYield,
    beta,
    eps,
    revenueGrowth,
    grossMargin,
    roe,
    rsi,
    week52High,
    week52Low,
    score,
    valueScore,
    growthScore,
    qualityScore,
    momentumScore,
    spark,
    description,
  };
}

export const SECURITIES: Security[] = RAW.map(buildSecurity);

export const SECURITY_MAP: Map<string, Security> = new Map(
  SECURITIES.map((security) => [security.symbol, security]),
);

export const UNIVERSE_COUNT = 13_214;
export const STOCK_COUNT = 11_402;
export const ETF_COUNT = 1_812;

export function getSecurity(symbol: string): Security | undefined {
  return SECURITY_MAP.get(symbol.toUpperCase());
}

export function searchSecurities(query: string, limit = 12): Security[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const starts: Security[] = [];
  const contains: Security[] = [];
  for (const security of SECURITIES) {
    const symbol = security.symbol.toLowerCase();
    const name = security.name.toLowerCase();
    if (symbol.startsWith(q)) starts.push(security);
    else if (name.includes(q) || symbol.includes(q)) contains.push(security);
  }
  return [...starts, ...contains].slice(0, limit);
}

export function getPeers(security: Security, limit = 6): Security[] {
  return SECURITIES.filter(
    (candidate) =>
      candidate.symbol !== security.symbol &&
      candidate.type === security.type &&
      (security.type === 'etf' || candidate.sector === security.sector),
  )
    .sort((a, b) => b.marketCap - a.marketCap)
    .slice(0, limit);
}

export function movers(kind: 'gainers' | 'losers' | 'active', limit = 8): Security[] {
  const pool = SECURITIES.filter((security) => security.type === 'stock');
  if (kind === 'gainers') return [...pool].sort((a, b) => b.changePct - a.changePct).slice(0, limit);
  if (kind === 'losers') return [...pool].sort((a, b) => a.changePct - b.changePct).slice(0, limit);
  return [...pool].sort((a, b) => b.volume - a.volume).slice(0, limit);
}

function makeSpark(seed: string, base: number, drift: number): number[] {
  const rand = mulberry32(hashString(seed));
  const values: number[] = [];
  let value = base * (1 - drift * 0.8);
  for (let i = 0; i < 20; i += 1) {
    value *= 1 + (rand() - 0.5) * 0.012 + drift / 260;
    values.push(round(value, 2));
  }
  values.push(base);
  return values;
}

export const INDICES: IndexQuote[] = [
  { symbol: 'SPY', name: 'S&P 500', price: 682.4, changePct: 0.64, spark: makeSpark('SPY', 682.4, 0.64) },
  { symbol: 'QQQ', name: 'Nasdaq 100', price: 612.8, changePct: 0.92, spark: makeSpark('QQQ', 612.8, 0.92) },
  { symbol: 'DIA', name: 'Dow 30', price: 468.2, changePct: 0.28, spark: makeSpark('DIA', 468.2, 0.28) },
  { symbol: 'IWM', name: 'Russell 2000', price: 248.6, changePct: -0.14, spark: makeSpark('IWM', 248.6, -0.14) },
];

export const SECTORS: SectorPerf[] = [
  { name: 'Technology', changePct: 1.42, weight: 31.4 },
  { name: 'Communication Services', changePct: 0.86, weight: 9.2 },
  { name: 'Consumer Discretionary', changePct: 0.54, weight: 10.1 },
  { name: 'Financials', changePct: 0.72, weight: 13.6 },
  { name: 'Health Care', changePct: -0.38, weight: 10.8 },
  { name: 'Industrials', changePct: 0.21, weight: 8.4 },
  { name: 'Energy', changePct: -1.12, weight: 3.6 },
  { name: 'Materials', changePct: -0.44, weight: 2.2 },
  { name: 'Utilities', changePct: 0.18, weight: 2.4 },
  { name: 'Real Estate', changePct: -0.62, weight: 2.1 },
  { name: 'Consumer Staples', changePct: 0.32, weight: 5.9 },
];

export const MARKET_STATS = {
  advancers: 3120,
  decliners: 2210,
  unchanged: 184,
  newHighs: 184,
  newLows: 96,
  upVolumePct: 68.4,
  downVolumePct: 31.6,
  lastUpdated: '16:00 ET',
};

export const NEWS: NewsItem[] = [
  {
    id: 'n1',
    symbol: 'NVDA',
    source: 'Desk Wire',
    headline: 'Nvidia extends gains as data-center orders stay ahead of supply',
    summary:
      'Channel checks point to another quarter of tight allocation for accelerated compute. The debate is no longer demand; it is how much of it is already in the price.',
    publishedAt: '2026-11-03T15:36:00Z',
    minutesAgo: 24,
  },
  {
    id: 'n2',
    symbol: 'AAPL',
    source: 'The Close',
    headline: 'Apple suppliers point to steadier iPhone build plans into year-end',
    summary:
      'Several Asian component makers flagged firmer schedules, a modest improvement on the cautious tone that framed the start of the quarter.',
    publishedAt: '2026-11-03T15:12:00Z',
    minutesAgo: 48,
  },
  {
    id: 'n3',
    symbol: 'SPY',
    source: 'Market Notes',
    headline: 'Treasury yields slip as traders weigh the next move on rates',
    summary:
      'The long end led a quiet rally. Equity breadth improved alongside it, though volume stayed below the 20-day average.',
    publishedAt: '2026-11-03T14:58:00Z',
    minutesAgo: 62,
  },
  {
    id: 'n4',
    symbol: 'TSLA',
    source: 'Desk Wire',
    headline: 'Tesla deliveries in focus ahead of the quarterly update',
    summary:
      'Investors are watching price cuts, mix and energy-storage attach rates. The stock has been a momentum name all year.',
    publishedAt: '2026-11-03T14:31:00Z',
    minutesAgo: 89,
  },
  {
    id: 'n5',
    symbol: 'JPM',
    source: 'Filings Desk',
    headline: 'Banks lead financials higher after stronger trading revenue',
    summary:
      'Capital-markets activity came in ahead of expectations across the large banks, helping the sector outperform the tape.',
    publishedAt: '2026-11-03T13:54:00Z',
    minutesAgo: 126,
  },
  {
    id: 'n6',
    symbol: 'XOM',
    source: 'Market Notes',
    headline: 'Oil steadies near recent range as supply risks offset demand worries',
    summary:
      'Crude held a narrow band while refiners lagged. Energy was the weakest sector at midday.',
    publishedAt: '2026-11-03T13:20:00Z',
    minutesAgo: 160,
  },
  {
    id: 'n7',
    symbol: 'LLY',
    source: 'Desk Wire',
    headline: "Lilly's obesity franchise draws fresh attention after prescription data",
    summary:
      'Weekly prescription trends showed another step up. Supply and pricing remain the two variables that matter for the franchise.',
    publishedAt: '2026-11-03T12:48:00Z',
    minutesAgo: 192,
  },
  {
    id: 'n8',
    symbol: 'MSFT',
    source: 'The Close',
    headline: "Microsoft's cloud backlog remains the number to watch",
    summary:
      'Azure growth is still the centre of the thesis. Margin commentary will matter as much as the headline number.',
    publishedAt: '2026-11-03T12:10:00Z',
    minutesAgo: 230,
  },
  {
    id: 'n9',
    symbol: 'AMD',
    source: 'Desk Wire',
    headline: 'AMD gains share in server chips, but valuation stays the debate',
    summary:
      'The company continues to take ground in accelerated compute. The stock trades at a premium that leaves little room for execution misses.',
    publishedAt: '2026-11-03T11:42:00Z',
    minutesAgo: 258,
  },
  {
    id: 'n10',
    symbol: 'COIN',
    source: 'Market Notes',
    headline: 'Crypto-linked equities rally with bitcoin',
    summary:
      'Exchange and mining names moved higher as digital assets extended gains. Correlations to the token price remain high.',
    publishedAt: '2026-11-03T11:05:00Z',
    minutesAgo: 295,
  },
  {
    id: 'n11',
    symbol: 'SPY',
    source: 'Desk Wire',
    headline: 'ETF flows: investors add to large-cap and dividend funds',
    summary:
      'Broad-market funds took in fresh money while sector funds saw modest outflows. Dividend strategies continue to gather assets.',
    publishedAt: '2026-11-03T10:34:00Z',
    minutesAgo: 326,
  },
  {
    id: 'n12',
    symbol: 'PLTR',
    source: 'The Close',
    headline: 'Palantir volatility rises after a sharp run',
    summary:
      'Options activity picked up as the stock approaches its next report. Valuation remains the most contested part of the story.',
    publishedAt: '2026-11-03T10:02:00Z',
    minutesAgo: 358,
  },
  {
    id: 'n13',
    symbol: 'WMT',
    source: 'Market Notes',
    headline: 'Retail earnings week puts the US consumer back in focus',
    summary:
      'Guidance from large retailers will be read for signs of trade-down behaviour and holiday inventory discipline.',
    publishedAt: '2026-11-03T09:40:00Z',
    minutesAgo: 380,
  },
  {
    id: 'n14',
    symbol: 'SPY',
    source: 'Desk Wire',
    headline: 'Futures point to a mixed open as earnings season winds down',
    summary:
      'Index futures were little changed before the bell. Macro data and a handful of large reports set the tone for the session.',
    publishedAt: '2026-11-03T09:05:00Z',
    minutesAgo: 415,
  },
];

export const EARNINGS: EarningsItem[] = [
  { symbol: 'PLTR', name: 'Palantir Technologies', date: '2026-11-04', timing: 'After close', epsEstimate: 0.17, revenueEstimate: 1.31e9 },
  { symbol: 'AMD', name: 'Advanced Micro Devices', date: '2026-11-04', timing: 'After close', epsEstimate: 1.06, revenueEstimate: 8.7e9 },
  { symbol: 'NVDA', name: 'NVIDIA Corporation', date: '2026-11-05', timing: 'After close', epsEstimate: 1.24, revenueEstimate: 54.2e9 },
  { symbol: 'UBER', name: 'Uber Technologies', date: '2026-11-05', timing: 'Before open', epsEstimate: 0.68, revenueEstimate: 12.4e9 },
  { symbol: 'DIS', name: 'The Walt Disney Company', date: '2026-11-06', timing: 'Before open', epsEstimate: 1.18, revenueEstimate: 22.8e9 },
  { symbol: 'LLY', name: 'Eli Lilly and Company', date: '2026-11-06', timing: 'Before open', epsEstimate: 6.42, revenueEstimate: 15.4e9 },
  { symbol: 'COST', name: 'Costco Wholesale', date: '2026-11-06', timing: 'After close', epsEstimate: 4.32, revenueEstimate: 68.2e9 },
];

export const DEMO_WATCHLIST = ['AAPL', 'NVDA', 'MSFT', 'SPY', 'LLY', 'JPM', 'AMD', 'QQQ'];

export const DEMO_POSITIONS: Position[] = [
  { symbol: 'AAPL', shares: 120, averageCost: 198.4 },
  { symbol: 'NVDA', shares: 80, averageCost: 142.2 },
  { symbol: 'MSFT', shares: 45, averageCost: 402.1 },
  { symbol: 'SPY', shares: 30, averageCost: 612.5 },
  { symbol: 'JPM', shares: 60, averageCost: 248.3 },
  { symbol: 'LLY', shares: 12, averageCost: 812.4 },
];

export const DEMO_ALERTS: AlertRule[] = [
  { id: 'a1', symbol: 'NVDA', kind: 'price_above', threshold: 200, channel: 'email', active: true, createdAt: '2026-10-28' },
  { id: 'a2', symbol: 'AAPL', kind: 'price_below', threshold: 220, channel: 'email', active: true, createdAt: '2026-10-24' },
  { id: 'a3', symbol: 'SPY', kind: 'change_pct', threshold: 1.5, channel: 'in_app', active: true, createdAt: '2026-10-19' },
  { id: 'a4', symbol: 'AMD', kind: 'rsi', threshold: 30, channel: 'in_app', active: false, createdAt: '2026-10-11' },
];

export const SAVED_SCREENS = [
  'Quality compounders',
  'High-yield dividend',
  'Momentum leaders',
  'Small-cap value',
];

export interface RangeConfig {
  points: number;
  step: 'minute' | 'day' | 'week' | 'month';
  label: string;
}

export const RANGE_CONFIG: Record<Range, RangeConfig> = {
  '1D': { points: 78, step: 'minute', label: '1 day' },
  '5D': { points: 65, step: 'minute', label: '5 days' },
  '1M': { points: 22, step: 'day', label: '1 month' },
  '3M': { points: 66, step: 'day', label: '3 months' },
  '6M': { points: 126, step: 'day', label: '6 months' },
  YTD: { points: 210, step: 'day', label: 'Year to date' },
  '1Y': { points: 252, step: 'day', label: '1 year' },
  '5Y': { points: 260, step: 'week', label: '5 years' },
  MAX: { points: 300, step: 'month', label: 'Max' },
};

function businessDays(count: number, end: Date): Date[] {
  const days: Date[] = [];
  const cursor = new Date(end);
  while (days.length < count) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) days.unshift(new Date(cursor));
    cursor.setDate(cursor.getDate() - 1);
  }
  return days;
}

function intradayLabels(range: Range, count: number): string[] {
  const sessions = range === '1D' ? 1 : 5;
  const perSession = Math.ceil(count / sessions);
  const labels: string[] = [];
  const start = new Date('2026-11-03T09:30:00');
  for (let session = 0; session < sessions; session += 1) {
    const day = new Date(start);
    day.setDate(day.getDate() - (sessions - 1 - session));
    const dayName = day.toLocaleDateString('en-US', { weekday: 'short' });
    for (let i = 0; i < perSession && labels.length < count; i += 1) {
      const minutes = 9 * 60 + 30 + i * 5;
      const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
      const mm = String(minutes % 60).padStart(2, '0');
      labels.push(range === '1D' ? `${hh}:${mm}` : `${dayName} ${hh}:${mm}`);
    }
  }
  return labels;
}

export function generateBars(symbol: string, range: Range): Bar[] {
  const security = getSecurity(symbol) ?? SECURITIES[0];
  const config = RANGE_CONFIG[range];
  const rand = mulberry32(hashString(`${symbol}:${range}`));
  const count = config.points;

  const volatility =
    range === '1D' ? 0.0018 : range === '5D' ? 0.0026 : range === 'MAX' ? 0.028 : 0.012;

  const closes: number[] = [];
  let value = 100;
  for (let i = 0; i < count; i += 1) {
    const drift = 0.00025 + (security.changePct / 100) * 0.0009;
    value *= 1 + drift + (rand() - 0.5) * volatility * 2;
    closes.push(value);
  }
  const scale = security.price / closes[closes.length - 1];
  const scaled = closes.map((close) => close * scale);

  let labels: string[];
  if (config.step === 'minute') {
    labels = intradayLabels(range, count);
  } else {
    const end = new Date('2026-11-03T12:00:00');
    if (config.step === 'day') {
      labels = businessDays(count, end).map((date) =>
        date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      );
    } else {
      const stepDays = config.step === 'week' ? 7 : 30;
      const dates: Date[] = [];
      const cursor = new Date(end);
      for (let i = 0; i < count; i += 1) {
        dates.unshift(new Date(cursor));
        cursor.setDate(cursor.getDate() - stepDays);
      }
      labels = dates.map((date) =>
        date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      );
    }
  }

  const baseVolume = security.volume / count;
  return scaled.map((close, index) => {
    const previous = index === 0 ? close * (1 - 0.002) : scaled[index - 1];
    const open = previous * (1 + (rand() - 0.5) * 0.004);
    const high = Math.max(open, close) * (1 + rand() * 0.0045);
    const low = Math.min(open, close) * (1 - rand() * 0.0045);
    const volume = Math.round(baseVolume * (0.45 + rand() * 1.35));
    return {
      time: labels[index] ?? '',
      open: round(open, 2),
      high: round(high, 2),
      low: round(low, 2),
      close: round(close, 2),
      volume,
    };
  });
}

export interface FinancialYear {
  year: string;
  revenue: number;
  grossProfit: number;
  operatingIncome: number;
  netIncome: number;
  eps: number;
  freeCashFlow: number;
}

export function getFinancials(security: Security): FinancialYear[] {
  const rand = mulberry32(hashString(`${security.symbol}:financials`));
  const [marginLow, marginHigh] = SECTOR_MARGIN[security.sector] ?? [0.08, 0.2];
  const netMargin = marginLow + rand() * (marginHigh - marginLow);
  const shares = security.marketCap / security.price;
  const latestRevenue = security.marketCap / (3 + rand() * 4);
  const years = ['2022', '2023', '2024', '2025', '2026'];
  const revenue: number[] = [latestRevenue];
  for (let i = 1; i < years.length; i += 1) {
    const growth = security.revenueGrowth / 100 + (rand() - 0.5) * 0.12;
    revenue.unshift(revenue[0] / (1 + growth));
  }
  return years.map((year, index) => {
    const rev = revenue[index];
    const grossMargin = (security.grossMargin || 35) / 100 + (rand() - 0.5) * 0.04;
    const grossProfit = rev * grossMargin;
    const operatingIncome = grossProfit * (0.55 + rand() * 0.25);
    const netIncome = rev * netMargin;
    return {
      year,
      revenue: Math.round(rev),
      grossProfit: Math.round(grossProfit),
      operatingIncome: Math.round(operatingIncome),
      netIncome: Math.round(netIncome),
      eps: Math.round((netIncome / shares) * 100) / 100,
      freeCashFlow: Math.round(netIncome * (0.8 + rand() * 0.45)),
    };
  });
}

export function getEarningsFor(symbol: string): EarningsItem | undefined {
  return EARNINGS.find((item) => item.symbol === symbol);
}

export function getNewsFor(symbol: string, limit = 5): NewsItem[] {
  const direct = NEWS.filter((item) => item.symbol === symbol);
  const rest = NEWS.filter((item) => item.symbol !== symbol);
  return [...direct, ...rest].slice(0, limit);
}

export function getSectorPerf(sector: string): SectorPerf | undefined {
  return SECTORS.find((item) => item.name === sector);
}

export const PORTFOLIO_CASH = 18_420;
